import styled from "@emotion/styled";
import { Dispatch, SetStateAction, useEffect, useState, useRef } from "react";
import { Dialog } from "@mui/material";
import theme from "../theme";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../locales";

// 다음 우편번호 API 타입 정의
declare global {
  interface Window {
    daum: {
      Postcode: new (options: any) => {
        embed: (element: HTMLElement | null) => void;
      };
    };
  }
}

const { md } = theme.screen;

type Prop<T> = {
  open: boolean;
  close: (data: any) => void;
  setForm: Dispatch<SetStateAction<T>>;
  top?: string;
  left?: string;
  width?: string;
};

/**
 * 다음 우편번호 검색 모달
 *
 * setForm의 프로퍼티 키에 address가 존재해야함.
 */
function DaumPost<T>({ open, close, setForm, width = "400px" }: Prop<T>) {
  const { width: windowWidth, height } = useWindowSize();
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const themeObj = {
    emphTextColor: theme.color.green_500,
    queryTextColor: theme.color.green_500,
  };

  // 다음 우편번호 API 직접 사용
  useEffect(() => {
    if (!open) return;

    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.async = true;
    
    script.onload = () => {
      if (window.daum && window.daum.Postcode) {
        setIsLoaded(true);
      }
    };

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      setIsLoaded(false);
    };
  }, [open]);

  const openPostcode = () => {
    if (!window.daum || !window.daum.Postcode) return;

    new window.daum.Postcode({
      oncomplete: function(data: any) {
        try {
          let fullAddress = data.address;
          let extraAddress = "";

          if (data.addressType === "R") {
            if (data.bname !== "") {
              extraAddress += data.bname;
            }
            if (data.buildingName !== "") {
              extraAddress +=
                extraAddress !== ""
                  ? `, ${data.buildingName}`
                  : data.buildingName;
            }
            fullAddress += extraAddress !== "" ? ` (${extraAddress})` : "";
          }

          setForm((prevState: T) => ({
            ...prevState,
            address: fullAddress,
          }));
          
          close(data);
        } catch (error) {
          console.error('DaumPost complete error:', error);
          close(data);
        }
      },
      onclose: function(state: any) {
        console.log('DaumPostcode onClose:', state);
        close(state);
      },
      theme: themeObj,
      width: windowWidth <= md ? "100%" : width,
      height: windowWidth <= md ? "100%" : "500px",
    }).embed(containerRef.current);
  };

  let vh = 0;
  useEffect(() => {
    try {
      vh = height * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    } catch (error) {
      console.error('DaumPost useEffect error:', error);
    }
  }, [height]);

  // Mount embed only after dialog transition finishes with longer delay
  useEffect(() => {
    if (open && isLoaded) {
      const timer = setTimeout(() => {
        openPostcode();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, isLoaded]);

  return (
    <Dialog
      open={open}
      keepMounted
      PaperProps={{
        sx: {
          width: windowWidth <= md ? "100vw" : width,
          borderRadius: windowWidth <= md ? "0px" : "20px",
          pb: "20px",
          margin: "0px",
          padding: "0px 10px 10px 10px",
          background: theme.color.neutral_100,
          // 외부 스크롤 제거
          overflow: 'hidden',
          // 모바일은 전체 높이, 데스크톱은 내용 높이에 맞춤
          height: windowWidth <= md ? '100vh' : 'auto',
        },
      }}
      onClose={(event, reason) => {
        console.log('DaumPost Dialog onClose:', { event, reason });
        close(event);
      }}
    >
      <div>
        <CloseBtn onClick={(e) => {
          console.log('DaumPost CloseBtn clicked');
          close(e);
        }}>{t.common.close}</CloseBtn>
        <div
          ref={containerRef}
          style={{
            width: "100%",
            // 내부 스크롤만 사용하도록 높이 고정
            height: windowWidth <= md ? `calc(var(--vh, 1vh) * 100)` : "550px",
            padding: "40px 0px 0px 0px",
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "center",
            overflow: 'auto',
          }}
        />
      </div>
    </Dialog>
  );
}

const CloseBtn = styled.button`
  position: absolute;
  right: 10px;
  margin: 12px 10px 10px 10px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 6;
`;

export default DaumPost;
